import { fetchWithAuth } from "../api/client";
import {
  EmptyResponse,
  Survey,
  ErrorResponse,
  SurveyWriteBase,
  UpdateSurveyPayload,
  SurveyQuestionAssignment,
  Question,
} from "../types";

const surveyTypeMap = {
  2: "generic",
  1: "entity",
} as const;

const languageMap = {
  1: "es",
  2: "ca",
} as const;

const orientationMap = {
  0: "niño",
  1: "familia",
} as const;

const statusMap = {
  0: "draft",
  1: "published",
  2: "archived",
} as const;

const statusReverseMap = {
  draft: 0,
  published: 1,
  archived: 2,
} as const;

export async function getSurveys(token: string): Promise<Survey[]> {
  const res = await fetchWithAuth("Survey", {
    method: "GET",
    token,
  });

  if (!res.ok) {
    throw new Error(`Error fetching surveys: ${res.statusText}`);
  }
  const data = await res.json();
  const allQuestionsRes = await fetchWithAuth("Questions", {
    method: "GET",
    token,
  });

  let allQuestions: any[] = [];
  if (allQuestionsRes.ok) {
    allQuestions = await allQuestionsRes.json();
  }

  return data.map((s: any): Survey => {
    const surveyQuestions: Question[] = (s.questions ?? [])
      .sort((a: any, b: any) => a.order - b.order)
      .map(
        (q: any): Question => ({
          id: q.questionId,
          text: {
            es: q.textEs,
            ca: q.textCa,
          },
          audioUrl: undefined,
          type: q.type,
          surveyId: undefined as any,
          order: q.order,
          isGeneric: q.isGeneric ?? false,
        })
      );

    return {
      id: s.id,
      title: { 
        es: s.titleEs, 
        ca: s.titleCa,
        ...(s.titleEn && { en: s.titleEn }),
        ...(s.titleAr && { ar: s.titleAr }),
      },
      description: { 
        es: s.descriptionEs, 
        ca: s.descriptionCa,
        ...(s.descriptionEn && { en: s.descriptionEn }),
        ...(s.descriptionAr && { ar: s.descriptionAr }),
      },
      type: surveyTypeMap[s.type as keyof typeof surveyTypeMap] ?? "generic",
      defaultLanguage:
        languageMap[s.defaultLanguage as keyof typeof languageMap] ?? "es",
      organizationId: s.organizationId,
      organizationName: s.organizationName,
      createdAt: s.createdAt,
      updatedAt: s.updatedAt ?? "",
      orientation:
        orientationMap[s.targetAudience as keyof typeof orientationMap] ??
        "niño",
      objetivo: s.targetBySurvey,
      status: statusMap[s.status as keyof typeof statusMap] ?? "draft",
      questions: surveyQuestions,
    };
  });
}

export async function getSurveyById(
  id: string,
  token: string
): Promise<Survey | ErrorResponse> {
  const res = await fetchWithAuth(`Survey/${id}`, {
    method: "GET",
    token,
  });

  if (!res.ok) {
    return {
      error: res.status,
      message: `Error fetching survey: ${res.statusText}`,
    };
  }

  const s = await res.json();

  const surveyQuestions: Question[] = (s.questions ?? [])
    .sort((a: any, b: any) => a.order - b.order)
    .map(
      (q: any): Question => ({
        id: q.questionId,
        text: {
          es: q.textEs,
          ca: q.textCa,
          ...(q.textEn && { en: q.textEn }),
          ...(q.textAr && { ar: q.textAr }),
        },
        audioUrl: undefined,
        type: q.type,
        surveyId: undefined as any,
        order: q.order,
        isGeneric: q.isGeneric ?? false,
      })
    );

  const survey: Survey = {
    id: s.id,
    title: { 
      es: s.titleEs, 
      ca: s.titleCa,
      ...(s.titleEn && { en: s.titleEn }),
      ...(s.titleAr && { ar: s.titleAr }),
    },
    description: { 
      es: s.descriptionEs, 
      ca: s.descriptionCa,
      ...(s.descriptionEn && { en: s.descriptionEn }),
      ...(s.descriptionAr && { ar: s.descriptionAr }),
    },
    type: surveyTypeMap[s.type as keyof typeof surveyTypeMap] ?? "generic",
    defaultLanguage:
      languageMap[s.defaultLanguage as keyof typeof languageMap] ?? "es",
    organizationId: s.organizationId,
    organizationName: s.organizationName,
    createdAt: s.createdAt,
    updatedAt: s.updatedAt ?? "",
    orientation:
      orientationMap[s.targetAudience as keyof typeof orientationMap] ?? "niño",
    objetivo: s.targetBySurvey,
    status: statusMap[s.status as keyof typeof statusMap] ?? "draft",
    questions: surveyQuestions,
  };

  return survey;
}

export async function createSurvey(
  payload: SurveyWriteBase,
  token: string
): Promise<Survey | ErrorResponse> {
  const apiPayload: any = {
    titleEs: payload.title.es,
    titleCa: payload.title.ca,
    titleEn: (payload.title as any)?.en || null,
    titleAr: (payload.title as any)?.ar || null,
    descriptionEs: payload.description?.es || null,
    descriptionCa: payload.description?.ca || null,
    descriptionEn: (payload.description as any)?.en || null,
    descriptionAr: (payload.description as any)?.ar || null,
    type: payload.type === "generic" ? 2 : 1,
    defaultLanguage: payload.defaultLanguage === "es" ? 1 : 2,
    status: statusReverseMap[payload.status as keyof typeof statusReverseMap],
    targetAudience: payload.orientation === "niño" ? 0 : 1,
    targetBySurvey: payload.objetivo,
  };

  if (payload.type !== "generic" && payload.organizationId) {
    apiPayload.organizationId = payload.organizationId;
  }

  if (payload.questions !== undefined) {
    apiPayload.questions = (
      payload.questions as SurveyQuestionAssignment[]
    ).map((q: SurveyQuestionAssignment) => ({
      questionId: q.questionId,
      order: q.order,
    }));
  }

  try {
    const res = await fetchWithAuth("Survey", {
      method: "POST",
      token,
      body: JSON.stringify(apiPayload),
    });

    if (!res.ok) {
      return {
        error: res.status,
        message: `Error creating survey: ${res.statusText}`,
      };
    }

    return await res.json();
  } catch (err: any) {
    return { error: 500, message: err.message };
  }
}

export async function updateSurvey(
  id: string,
  payload: UpdateSurveyPayload,
  token: string
): Promise<Survey | EmptyResponse | ErrorResponse> {
  const apiPayload: any = {
    id,
    titleEs: payload.title.es,
    titleCa: payload.title.ca,
    titleEn: (payload.title as any)?.en || null,
    titleAr: (payload.title as any)?.ar || null,
    descriptionEs: payload.description?.es || null,
    descriptionCa: payload.description?.ca || null,
    descriptionEn: (payload.description as any)?.en || null,
    descriptionAr: (payload.description as any)?.ar || null,
    type: payload.type === "generic" ? 2 : 1,
    defaultLanguage: payload.defaultLanguage === "es" ? 1 : 2,
    status: statusReverseMap[payload.status as keyof typeof statusReverseMap],
    targetAudience: payload.orientation === "niño" ? 0 : 1,
    targetBySurvey: payload.objetivo,
  };

  if (payload.type !== "generic" && payload.organizationId) {
    apiPayload.organizationId = payload.organizationId;
  }

  if (typeof (payload as any).IsCodeActive === "boolean") {
    apiPayload.isCodeActive = (payload as any).IsCodeActive;
  }

  if (payload.questions !== undefined) {
    apiPayload.questions = (
      payload.questions as SurveyQuestionAssignment[]
    ).map((q: SurveyQuestionAssignment) => ({
      questionId: q.questionId,
      order: q.order,
    }));
  }

  const res = await fetchWithAuth(`Survey/${id}`, {
    method: "PUT",
    token,
    body: JSON.stringify(apiPayload),
  });

  if (!res.ok) {
    return {
      error: res.status,
      message: `Error updating survey: ${res.statusText}`,
    };
  }

  if (res.status === 204) {
    return {} as EmptyResponse;
  }

  const text = await res.text();
  if (!text || text.trim().length === 0) {
    return {} as EmptyResponse;
  }

  try {
    return JSON.parse(text) as Survey;
  } catch {
    return {} as EmptyResponse;
  }
}

export async function deleteSurvey(
  surveyToDelete: Survey,
  token: string
): Promise<{ ok: true } | ErrorResponse> {
  try {
    const res = await fetchWithAuth(`Survey/${surveyToDelete.id}`, {
      method: "DELETE",
      token,
    });
    // fetchWithAuth throws on !ok; if we're here, it's ok
    return { ok: true };
  } catch (err: any) {
    return {
      error: 500,
      message: err?.message || "Error deleting survey",
    };
  }
}
